/**
 * Sync engine for FlightSync PWA.
 *
 * Responsibilities:
 * - POST /api/flight-data/ingest-rich for each pending flight
 * - Update IndexedDB record with synced=true and server flight_id
 * - Expose subscribe(listener) to report sync progress
 */
import pako from 'pako';
import { listPending, updateFlight } from './flightSyncDb';
import { notify } from './flightSyncPush';

const API = process.env.REACT_APP_BACKEND_URL || '';
const GPS_GZIP_THRESHOLD = 1000; // compress when > 1000 points

let _running = false;
let _listeners = new Set();

export function subscribe(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function emit(evt) {
  _listeners.forEach((fn) => {
    try { fn(evt); } catch { /* ignore */ }
  });
}

export async function validateToken(token) {
  const res = await fetch(`${API}/api/flight-data/validate-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Token invalido');
  }
  return res.json();
}

async function postFlight(flight, token) {
  const gpsTrack = flight.gps_track || [];
  const payload = {
    start_time: flight.start_time,
    end_time: flight.end_time,
    duration_seconds: flight.duration_seconds,
    distance_meters: flight.distance_meters,
    drone_model: flight.drone_model,
    drone_serial: flight.drone_serial,
    pilot_name: flight.pilot_name,
    max_altitude_meters: flight.max_altitude_meters,
    max_speed_ms: flight.max_speed_ms,
    battery_start_pct: flight.battery_start_pct,
    battery_end_pct: flight.battery_end_pct,
    home_lat: flight.home_lat,
    home_lng: flight.home_lng,
    notes: flight.notes || '',
    file_name: flight.file_name || '',
    app_version: '1.0.0',
    source: 'pwa_flightsync',
    rtk_summary: flight.rtk_summary || null,
  };
  // Attach raw_log_base64 if present (e.g., .dat binary upload)
  if (flight.raw_log_base64) {
    payload.raw_log_base64 = flight.raw_log_base64;
  }
  // Gzip + base64 the GPS track when large to reduce payload size
  if (gpsTrack.length > GPS_GZIP_THRESHOLD) {
    const json = JSON.stringify(gpsTrack);
    const gz = pako.gzip(json);
    payload.gps_track_gzip_b64 = toBase64(gz);
    payload.gps_track_count = gpsTrack.length;
  } else {
    payload.gps_track = gpsTrack;
  }
  const res = await fetch(`${API}/api/flight-data/ingest-rich`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Flight-Token': token },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

function toBase64(u8) {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) {
    bin += String.fromCharCode.apply(null, u8.subarray(i, i + chunk));
  }
  return btoa(bin);
}

export async function flushQueue(token) {
  if (_running) return { ran: false };
  if (!token) return { ran: false, error: 'Sem token' };
  if (!navigator.onLine) { emit({ type: 'offline' }); return { ran: false, error: 'offline' }; }
  _running = true;
  emit({ type: 'flush:start' });
  let ok = 0, fail = 0;
  try {
    const pending = await listPending();
    for (const f of pending) {
      emit({ type: 'flush:item', flight_id: f.id });
      try {
        const resp = await postFlight(f, token);
        await updateFlight(f.id, {
          synced: true,
          server_flight_id: resp.flight_id,
          synced_at: new Date().toISOString(),
        });
        ok++;
        emit({ type: 'flush:ok', flight_id: f.id, server_flight_id: resp.flight_id });
        // Local push notification on successful sync
        notify('Voo sincronizado', `${f.drone_model || 'DJI'} · ${((f.distance_meters || 0) / 1000).toFixed(2)} km enviado ao servidor`);
      } catch (e) {
        fail++;
        await updateFlight(f.id, { last_error: String(e.message || e) });
        emit({ type: 'flush:fail', flight_id: f.id, error: String(e.message || e) });
      }
    }
  } finally {
    _running = false;
    emit({ type: 'flush:end', ok, fail });
  }
  return { ran: true, ok, fail };
}
